import { useMemo, useState } from 'react';
import ReportsNav from '../../components/ReportsNav';
import { reportsApi, type DebtAgingDto } from '../../api/reports';
import MoneyCell from '../../components/MoneyCell';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useReportScreen } from '../../components/ScreenContext';
import { formatMoney } from '../../utils/formatMoney';

export default function DebtAging() {
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [type, setType] = useState('receivable');
  const [data, setData] = useState<DebtAgingDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await reportsApi.debtAging({ asOf, type });
      setData(result);
    } catch (e: any) {
      setError(e.message || 'Failed to generate aging report');
    } finally {
      setLoading(false);
    }
  };

  // Screen context for the AI copilot (house pattern: JournalList) —
  // the outstanding total + person count ride in the summary so data changes re-report.
  const recordCount = data?.personDetails.length ?? 0;
  const screenPreview = useMemo(() =>
    (data?.personDetails ?? []).slice(0, 10).map(p =>
      `${p.person.name} (${p.person.code}): viso ${formatMoney(p.totalOutstanding)}, is ju 90+d ${formatMoney(p.bucketOver90)}`.substring(0, 120)
    ), [data]);

  useReportScreen(
    // Data branch quotes the GENERATED report's own type/asOfDate (not the UI
    // filter inputs — changing a filter before Generate must not relabel old data).
    data
      ? `Skolu senumo ataskaita (${data.type === 'receivable' ? 'gautinos' : 'moketinos'}, ${data.asOfDate}): viso ${formatMoney(data.totalOutstanding)}, ${recordCount} asmenu`
      : `Skolu senumo ataskaita (${type === 'receivable' ? 'gautinos' : 'moketinos'}, ${asOf}): neugeneruota`,
    {
      recordCount,
      filters: { datai: asOf, tipas: type },
      preview: screenPreview,
    },
  );

  return (
    <div className="space-y-4">
      <ReportsNav />
      <h1>Skolų senumo ataskaita</h1>

      <div className="filters" style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
        <label>
          Datai: <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
        </label>
        <label>
          Type:
          <select value={type} onChange={e => setType(e.target.value)}>
            <option value="receivable">Gautinos</option>
            <option value="payable">Mokėtinos</option>
          </select>
        </label>
        <button onClick={handleGenerate} disabled={loading}>
          {loading ? <LoadingSpinner /> : 'Generate'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {data && (
        <>
          {/* Summary Buckets */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h2>Senumo santrauka</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Grupė</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th style={{ textAlign: 'right' }}>Persons</th>
                </tr>
              </thead>
              <tbody>
                {data.buckets.map((bucket, i) => (
                  <tr key={i}>
                    <td>{bucket.bucketName}</td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={bucket.amount} /></td>
                    <td style={{ textAlign: 'right' }}>{bucket.personCount}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td><strong>Viso neapmokėta</strong></td>
                  <td style={{ textAlign: 'right' }}><strong><MoneyCell value={data.totalOutstanding} /></strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Person Details */}
          <div className="card">
            <h2>Pagal asmenis</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Current</th>
                  <th style={{ textAlign: 'right' }}>1-30d</th>
                  <th style={{ textAlign: 'right' }}>31-60d</th>
                  <th style={{ textAlign: 'right' }}>61-90d</th>
                  <th style={{ textAlign: 'right' }}>90+d</th>
                </tr>
              </thead>
              <tbody>
                {data.personDetails.map((person, i) => (
                  <tr key={i}>
                    <td>{person.person.name} ({person.person.code})</td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.totalOutstanding} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.currentAmount} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.bucket1to30} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.bucket31to60} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.bucket61to90} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={person.bucketOver90} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
